import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Wrench } from "lucide-react";

import PageHeader from "../../components/PageHeader";
import { COLORS } from "../../constants";
import { optionsService } from "../../services/optionsService";
import {
  desenvolvedorService,
  type AguardandoBordaOrfaoItem,
  type BordaEmFaltaItem,
  type OrfaoAcaoModo,
} from "../../services/desenvolvedorService";
import ControlePedidoDev from "./ControlePedidoDev";
import MapeamentoSaboresDev from "./MapeamentoSaboresDev";
import MapeamentoProdutosDev from "./MapeamentoProdutosDev";
import FichaDiariaDev from "./FichaDiariaDev";
import { BrazilDate } from "../../utils/BrazilDate";

const qkBordas = ["desenvolvedor", "bordas-em-falta"] as const;
const qkOrfaos = ["desenvolvedor", "aguardando-borda-orfaos"] as const;

type ConfirmKind = "bordas" | "orfaos";
type TabKey = "controle" | "mapeamento" | "correcoes" | "ficha";
type MapeamentoKind = "sabores" | "produtos";

type OrfaoConfirmTarget = {
  lojaId: number;
  lojaNome: string;
  data: string;
  modo: OrfaoAcaoModo;
  quantidade: number;
};

function labelTipoFila(tipo: AguardandoBordaOrfaoItem["tipo"]): string {
  switch (tipo) {
    case "salgada":
      return "fila salgada";
    case "doce":
      return "fila doce";
    case "salao_salgada":
      return "salão salgada";
    case "salao_doce":
      return "salão doce";
  }
}

function labelFilaBordaEmFalta(r: BordaEmFaltaItem): string {
  if (r.filaSalgadaId != null) return `fila salgada #${r.filaSalgadaId}`;
  if (r.filaDoceId != null) return `fila doce #${r.filaDoceId}`;
  if (r.filaSalaoSalgadaId != null)
    return `salão salgada #${r.filaSalaoSalgadaId}`;
  if (r.filaSalaoDoceId != null) return `salão doce #${r.filaSalaoDoceId}`;
  return "sem fila vinculada";
}

function formatDiaBr(isoDia: string): string {
  const [y, m, d] = isoDia.split("-").map(Number);
  if (!y || !m || !d) return isoDia;
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
}

function diaOfOrfao(r: AguardandoBordaOrfaoItem): string {
  if (r.dia) return r.dia;
  const d = new Date(r.createdAt);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const FerramentasDesenvolvedor: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>("controle");
  const [mapeamentoKind, setMapeamentoKind] =
    useState<MapeamentoKind>("produtos");
  const [lojaFiltro, setLojaFiltro] = useState<number | "">("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState<ConfirmKind>("bordas");
  const [lojaAlvo, setLojaAlvo] = useState<number | null>(null);
  const [orfaoAlvo, setOrfaoAlvo] = useState<OrfaoConfirmTarget | null>(null);

  const hojeIso = BrazilDate.todayISO();

  const lojasQuery = useQuery({
    queryKey: ["lojas"],
    queryFn: () => optionsService.getLojas(),
  });

  const bordasQuery = useQuery({
    queryKey: [...qkBordas, lojaFiltro],
    queryFn: () =>
      desenvolvedorService.listarBordasEmFalta(
        lojaFiltro === "" ? undefined : lojaFiltro,
      ),
    enabled: tab === "correcoes",
  });

  const orfaosQuery = useQuery({
    queryKey: [...qkOrfaos, lojaFiltro],
    queryFn: () =>
      desenvolvedorService.listarAguardandoBordaOrfaos(
        lojaFiltro === "" ? undefined : lojaFiltro,
      ),
    enabled: tab === "correcoes",
  });

  const invalidateAll = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: qkBordas });
    void queryClient.invalidateQueries({ queryKey: qkOrfaos });
  }, [queryClient]);

  const liberarBordasMutation = useMutation({
    mutationFn: (lojaId: number) =>
      desenvolvedorService.liberarBordasEmFalta(lojaId),
    onSuccess: (result) => {
      const filas =
        result.filasSalgadaLiberadas +
        result.filasDoceLiberadas +
        (result.filasSalaoSalgadaLiberadas ?? 0) +
        (result.filasSalaoDoceLiberadas ?? 0);
      toast.success(
        `Correção aplicada na loja ${result.lojaId}: ${result.bordasEmFaltaRemovidas} borda(s) em falta removida(s); ` +
          `${filas} fila(s) devolvida(s) para pending.`,
      );
      setConfirmOpen(false);
      setLojaAlvo(null);
      setOrfaoAlvo(null);
      invalidateAll();
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao liberar bordas em falta.");
    },
  });

  const orfaosMutation = useMutation({
    mutationFn: (target: OrfaoConfirmTarget) =>
      desenvolvedorService.liberarAguardandoBordaOrfaos({
        lojaId: target.lojaId,
        data: target.data,
        modo: target.modo,
      }),
    onSuccess: (result) => {
      const acao =
        result.modo === "limpar"
          ? "limpadas (cancelled)"
          : "devolvidas para a fila (pending)";
      toast.success(
        `${result.totalLiberadas} órfã(s) ${acao} — loja ${result.lojaId}, dia ${formatDiaBr(result.data)}.`,
      );
      setConfirmOpen(false);
      setLojaAlvo(null);
      setOrfaoAlvo(null);
      invalidateAll();
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao processar órfãos.");
    },
  });

  const isPending =
    liberarBordasMutation.isPending || orfaosMutation.isPending;

  const itens = useMemo(() => bordasQuery.data ?? [], [bordasQuery.data]);
  const orfaos = useMemo(() => orfaosQuery.data ?? [], [orfaosQuery.data]);

  const porLoja = useMemo(() => {
    const map = new Map<number, BordaEmFaltaItem[]>();
    for (const item of itens) {
      const list = map.get(item.lojaId) ?? [];
      list.push(item);
      map.set(item.lojaId, list);
    }
    return map;
  }, [itens]);

  /** lojaId → dia → itens */
  const orfaosPorLojaDia = useMemo(() => {
    const map = new Map<
      number,
      { lojaNome: string; dias: Map<string, AguardandoBordaOrfaoItem[]> }
    >();
    for (const item of orfaos) {
      const dia = diaOfOrfao(item);
      let lojaEntry = map.get(item.lojaId);
      if (!lojaEntry) {
        lojaEntry = { lojaNome: item.lojaNome, dias: new Map() };
        map.set(item.lojaId, lojaEntry);
      }
      const list = lojaEntry.dias.get(dia) ?? [];
      list.push(item);
      lojaEntry.dias.set(dia, list);
    }
    return map;
  }, [orfaos]);

  const abrirConfirmacaoBordas = useCallback((lojaId: number) => {
    setLojaAlvo(lojaId);
    setOrfaoAlvo(null);
    setConfirmKind("bordas");
    setConfirmOpen(true);
  }, []);

  const abrirConfirmacaoOrfaos = useCallback((target: OrfaoConfirmTarget) => {
    setOrfaoAlvo(target);
    setLojaAlvo(target.lojaId);
    setConfirmKind("orfaos");
    setConfirmOpen(true);
  }, []);

  const confirmarLiberacao = useCallback(() => {
    if (confirmKind === "orfaos") {
      if (!orfaoAlvo) return;
      orfaosMutation.mutate(orfaoAlvo);
      return;
    }
    if (lojaAlvo == null) return;
    liberarBordasMutation.mutate(lojaAlvo);
  }, [
    confirmKind,
    orfaoAlvo,
    lojaAlvo,
    liberarBordasMutation,
    orfaosMutation,
  ]);

  const lojaAlvoNome =
    lojaAlvo != null
      ? (lojasQuery.data as { id: number; nome: string }[] | undefined)?.find(
          (l) => l.id === lojaAlvo,
        )?.nome ?? `Loja ${lojaAlvo}`
      : "";

  return (
    <main className="mx-auto max-w-[1100px] px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Ferramentas de desenvolvedor"
        description="Suporte em produção — exclusivo do perfil Desenvolvedor."
      />

      <div className="mb-6 flex flex-wrap gap-2 border-b border-neutral-200 pb-2">
        <button
          type="button"
          onClick={() => setTab("controle")}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            tab === "controle"
              ? "bg-red-700 text-white"
              : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
          }`}
        >
          Controle de pedido
        </button>
        <button
          type="button"
          onClick={() => setTab("mapeamento")}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            tab === "mapeamento"
              ? "text-white"
              : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
          }`}
          style={
            tab === "mapeamento"
              ? { backgroundColor: COLORS.PRIMARY }
              : undefined
          }
        >
          Mapeamento
        </button>
        <button
          type="button"
          onClick={() => setTab("ficha")}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            tab === "ficha"
              ? "text-white"
              : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
          }`}
          style={
            tab === "ficha" ? { backgroundColor: COLORS.PRIMARY } : undefined
          }
        >
          Ficha diária
        </button>
        <button
          type="button"
          onClick={() => setTab("correcoes")}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            tab === "correcoes"
              ? "text-white"
              : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
          }`}
          style={
            tab === "correcoes" ? { backgroundColor: COLORS.PRIMARY } : undefined
          }
        >
          Correções (bordas)
        </button>
      </div>

      {tab === "controle" ? (
        <ControlePedidoDev />
      ) : tab === "mapeamento" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setMapeamentoKind("produtos")}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                mapeamentoKind === "produtos"
                  ? "text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
              style={
                mapeamentoKind === "produtos"
                  ? { backgroundColor: COLORS.PRIMARY }
                  : undefined
              }
            >
              Produtos
            </button>
            <button
              type="button"
              onClick={() => setMapeamentoKind("sabores")}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                mapeamentoKind === "sabores"
                  ? "text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
              style={
                mapeamentoKind === "sabores"
                  ? { backgroundColor: COLORS.PRIMARY }
                  : undefined
              }
            >
              Sabores (pizza)
            </button>
          </div>
          {mapeamentoKind === "produtos" ? (
            <MapeamentoProdutosDev />
          ) : (
            <MapeamentoSaboresDev />
          )}
        </div>
      ) : tab === "ficha" ? (
        <FichaDiariaDev />
      ) : (
        <>
          <section className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <p className="font-semibold">Atenção</p>
            <p className="mt-1">
              Estas ações devolvem filas em <code>aguardando_borda</code> para{" "}
              <code>pending</code> e destravam o pizzaiolo. Use só para corrigir
              inconsistências de produção.
            </p>
          </section>

          <section className="mt-6 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <label className="text-sm font-medium text-neutral-700">
                Filtrar loja
                <select
                  className="mt-1 block min-w-[200px] rounded-md border border-neutral-300 px-3 py-2 text-sm"
                  value={lojaFiltro}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLojaFiltro(v === "" ? "" : Number(v));
                  }}
                >
                  <option value="">Todas</option>
                  {(
                    lojasQuery.data as { id: number; nome: string }[] | undefined
                  )?.map((loja) => (
                    <option key={loja.id} value={loja.id}>
                      {loja.nome}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <h2 className="mb-2 text-lg font-bold text-neutral-900">
              Bordas em falta registradas
            </h2>
            <p className="mb-4 text-sm text-neutral-600">
              Registros em <code>borda_em_falta</code> (pizzaiolo pulou por falta
              de borda). Liberar remove o bloqueio e devolve as filas
              vinculadas.
            </p>

            {bordasQuery.isLoading ? (
              <p className="text-neutral-600">Carregando…</p>
            ) : bordasQuery.isError ? (
              <p className="text-red-600">Não foi possível carregar.</p>
            ) : itens.length === 0 ? (
              <p className="text-neutral-600">
                Nenhuma borda em falta registrada
                {lojaFiltro !== "" ? " nesta loja" : ""}.
              </p>
            ) : (
              <div className="space-y-6">
                {Array.from(porLoja.entries()).map(([lojaId, rows]) => (
                  <div key={lojaId}>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-base font-bold text-neutral-900">
                        {rows[0]?.lojaNome ?? `Loja ${lojaId}`} ({rows.length})
                      </h3>
                      <button
                        type="button"
                        onClick={() => abrirConfirmacaoBordas(lojaId)}
                        className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white"
                        style={{ backgroundColor: COLORS.PRIMARY }}
                      >
                        Liberar bordas em falta desta loja
                      </button>
                    </div>
                    <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200 text-sm">
                      {rows.map((r) => (
                        <li key={r.id} className="px-3 py-2">
                          <span className="font-medium">{r.bordaNome}</span>
                          <span className="ml-2 text-neutral-500">
                            {labelFilaBordaEmFalta(r)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="mt-6 rounded-lg border border-red-200 bg-white p-6 shadow-sm">
            <h2 className="mb-2 text-lg font-bold text-neutral-900">
              Órfãos: aguardando borda sem registro
            </h2>
            <p className="mb-4 text-sm text-neutral-600">
              Agrupados por dia.{" "}
              <strong>Hoje</strong>: voltar à fila do pizzaiolo (
              <code>pending</code>). <strong>Dias antigos</strong>: limpar (
              <code>cancelled</code>) — não voltam à produção.
            </p>

            {orfaosQuery.isLoading ? (
              <p className="text-neutral-600">Carregando…</p>
            ) : orfaosQuery.isError ? (
              <p className="text-red-600">Não foi possível carregar órfãos.</p>
            ) : orfaos.length === 0 ? (
              <p className="text-neutral-600">
                Nenhum órfão encontrado
                {lojaFiltro !== "" ? " nesta loja" : ""}.
              </p>
            ) : (
              <div className="space-y-8">
                {Array.from(orfaosPorLojaDia.entries()).map(
                  ([lojaId, lojaEntry]) => {
                    const diasOrdenados = Array.from(lojaEntry.dias.entries()).sort(
                      (a, b) => b[0].localeCompare(a[0]),
                    );
                    const totalLoja = diasOrdenados.reduce(
                      (acc, [, rows]) => acc + rows.length,
                      0,
                    );
                    return (
                      <div key={lojaId}>
                        <h3 className="mb-3 text-base font-bold text-neutral-900">
                          {lojaEntry.lojaNome} ({totalLoja})
                        </h3>
                        <div className="space-y-4">
                          {diasOrdenados.map(([dia, rows]) => {
                            const isHoje = dia === hojeIso;
                            return (
                              <div
                                key={`${lojaId}-${dia}`}
                                className="rounded-md border border-red-100"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-red-100 bg-red-50/60 px-3 py-2">
                                  <div className="text-sm font-semibold text-neutral-900">
                                    {formatDiaBr(dia)}
                                    {isHoje ? (
                                      <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold uppercase text-emerald-800">
                                        Hoje
                                      </span>
                                    ) : null}
                                    <span className="ml-2 font-normal text-neutral-500">
                                      ({rows.length})
                                    </span>
                                  </div>
                                  {isHoje ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        abrirConfirmacaoOrfaos({
                                          lojaId,
                                          lojaNome: lojaEntry.lojaNome,
                                          data: dia,
                                          modo: "voltar_fila",
                                          quantidade: rows.length,
                                        })
                                      }
                                      className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white"
                                      style={{ backgroundColor: COLORS.PRIMARY }}
                                    >
                                      Voltar à fila
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        abrirConfirmacaoOrfaos({
                                          lojaId,
                                          lojaNome: lojaEntry.lojaNome,
                                          data: dia,
                                          modo: "limpar",
                                          quantidade: rows.length,
                                        })
                                      }
                                      className="rounded-lg bg-red-700 px-3 py-1.5 text-sm font-semibold text-white"
                                    >
                                      Limpar
                                    </button>
                                  )}
                                </div>
                                <ul className="divide-y divide-neutral-100 text-sm">
                                  {rows.map((r) => (
                                    <li
                                      key={`${r.tipo}-${r.filaId}`}
                                      className="flex flex-wrap items-baseline gap-x-2 px-3 py-2"
                                    >
                                      <span className="font-medium">
                                        {r.bordaNome}
                                      </span>
                                      <span className="text-neutral-500">
                                        pedido #{r.displayId} ·{" "}
                                        {labelTipoFila(r.tipo)} #{r.filaId} ·{" "}
                                        {r.canal === "salao"
                                          ? "salão"
                                          : "cardápio"}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            )}
          </section>

          {confirmOpen && lojaAlvo != null && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-liberar-title"
            >
              <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                <div className="mb-3 flex items-center gap-2 text-neutral-900">
                  <Wrench className="h-5 w-5" aria-hidden />
                  <h2 id="confirm-liberar-title" className="text-lg font-bold">
                    {confirmKind === "orfaos" && orfaoAlvo?.modo === "limpar"
                      ? "Confirmar limpeza"
                      : "Confirmar liberação"}
                  </h2>
                </div>
                <p className="text-sm text-neutral-700">
                  {confirmKind === "orfaos" && orfaoAlvo ? (
                    orfaoAlvo.modo === "limpar" ? (
                      <>
                        Isso vai <strong>cancelar</strong>{" "}
                        {orfaoAlvo.quantidade} órfã(s) de{" "}
                        <strong>{orfaoAlvo.lojaNome}</strong> do dia{" "}
                        <strong>{formatDiaBr(orfaoAlvo.data)}</strong>. Eles não
                        voltam à fila. Continuar?
                      </>
                    ) : (
                      <>
                        Isso vai devolver{" "}
                        <strong>{orfaoAlvo.quantidade}</strong> órfã(s) de{" "}
                        <strong>{orfaoAlvo.lojaNome}</strong> (hoje) para a fila
                        do pizzaiolo. Continuar?
                      </>
                    )
                  ) : (
                    <>
                      Isso vai remover todas as bordas em falta de{" "}
                      <strong>{lojaAlvoNome}</strong> e devolver filas presas em
                      aguardando borda para a fila do pizzaiolo. Deseja
                      continuar?
                    </>
                  )}
                </p>
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium"
                    disabled={isPending}
                    onClick={() => {
                      setConfirmOpen(false);
                      setLojaAlvo(null);
                      setOrfaoAlvo(null);
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                    disabled={isPending}
                    onClick={confirmarLiberacao}
                  >
                    {isPending
                      ? "Aplicando…"
                      : confirmKind === "orfaos" && orfaoAlvo?.modo === "limpar"
                        ? "Sim, limpar"
                        : "Sim, liberar"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default FerramentasDesenvolvedor;
